import { Box, NumberInput, Text } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { computeLateFeeExample } from "./late-fee-example";
import type { ExampleInput } from "./late-fee-example";
import { settingsClasses as classes, unitProps } from "./settings-ui";

const money = (v: number) => `${v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₮`;

type Props = {
  config: ExampleInput;
};

/** Баруун aside: хадгалаагүй тохиргоогоор шууд бодогдох жишээ. Оролт нь зөвхөн local state, хадгалагдахгүй. */
export function LateFeeExampleCard({ config }: Props) {
  const { t } = useTranslation("settings");
  const [amount, setAmount] = useState<number | string>(1000000);
  const [days, setDays] = useState<number | string>(10);

  const example = computeLateFeeExample(config, Number(amount) || 0, Number(days) || 0);
  // Backend-ийн томьёо тодорхойгүй (ESCALATING) эсвэл арга сонгоогүй үед картыг нууна.
  const isUnknownType = !example;

  return (
    <>
      {!isUnknownType && (
        <Box component="section" className={`${classes.card} ${classes.exampleCard}`}>
          <Box className={classes.heading}>
            <Text component="span" className={classes.exampleTitle}>{t("example.title")}</Text>
            <Text component="span" className={classes.sub}>{t("example.subtitle")}</Text>
          </Box>
          <Box className={classes.exampleInputs}>
            <NumberInput
              label={t("example.invoice")}
              value={amount}
              onChange={setAmount}
              min={0}
              thousandSeparator=","
              size="sm"
              {...unitProps("₮")}
            />
            <NumberInput
              label={t("example.overdue")}
              value={days}
              onChange={setDays}
              min={0}
              allowDecimal={false}
              size="sm"
              {...unitProps(t("unit.days"))}
            />
          </Box>
          <Box className={classes.exampleLine}>
            <Text span>{t("example.penaltyDays")}</Text>
            <Text span className={classes.exampleValue}>
              {example.daysPastDue} − {example.graceDays} = {Math.max(example.penaltyDays, 0)}
            </Text>
          </Box>
          {example.perDayFee !== null && (
            <Box className={classes.exampleLine}>
              <Text span>{config.feeCalculationType === "COMPOUND_DAILY" ? t("example.firstDayFee") : t("example.perDayFee")}</Text>
              <Text span className={classes.exampleValue}>{money(example.perDayFee)}</Text>
            </Box>
          )}
          {example.capRateAmount !== null && (
            <Box className={classes.exampleLine}>
              <Text span>{t("example.capRate", { rate: example.capRatePercent })}</Text>
              <Text span className={classes.exampleValue}>{money(example.capRateAmount)}</Text>
            </Box>
          )}
          {example.capAmount !== null && (
            <Box className={classes.exampleLine}>
              <Text span>{t("example.capAmount")}</Text>
              <Text span className={classes.exampleValue}>{money(example.capAmount)}</Text>
            </Box>
          )}
          {example.minAmount !== null && (
            <Box className={classes.exampleLine}>
              <Text span>{t("example.minAmount")}</Text>
              <Text span className={classes.exampleValue}>{money(example.minAmount)}</Text>
            </Box>
          )}
          <Box className={`${classes.exampleLine} ${classes.exampleTotal}`}>
            <Text span>{t("example.result")}</Text>
            <Text span className={classes.exampleValue}>{money(example.fee)}</Text>
          </Box>
        </Box>
      )}
      <Box className={classes.note}>{t("example.note")}</Box>
    </>
  );
}
